import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Clock, AlertTriangle, Sparkles, CreditCard, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function TrialBanner({ subscription, onUpgrade, onDismiss }) {
  const { daysRemaining, isInTrial, isInGrace, isExpired, pricing } = subscription;
  
  // Don't show banner if fully active/paid
  if (subscription.isActive) {
    return null;
  }

  // Days 1-23: Subtle info banner (dismissible)
  if (isInTrial && daysRemaining > 7) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-6"
        >
          <Alert className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            <AlertDescription className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-emerald-900 font-medium">
                  ✨ Free trial active — {daysRemaining} days remaining
                </p>
                <p className="text-sm text-emerald-700 mt-1">
                  Enjoy full access to all features. Upgrade anytime to continue after your trial.
                </p>
              </div>
              {onDismiss && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDismiss}
                  className="ml-4 text-emerald-600 hover:text-emerald-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </AlertDescription>
          </Alert>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Days 24-30: Prominent upgrade reminder
  if (isInTrial && daysRemaining <= 7 && daysRemaining > 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <Alert className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300">
          <Clock className="h-5 w-5 text-orange-600" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-orange-900 font-bold text-lg">
                ⏰ Trial ending in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
              </p>
              <p className="text-sm text-orange-800 mt-1">
                Upgrade now to keep full access to your estate management tools.
                Plan: <span className="font-semibold">{pricing.label}</span> ({pricing.units})
              </p>
            </div>
            <Button
              onClick={onUpgrade}
              className="ml-4 bg-[#1883FF] hover:bg-[#0066CC] text-white rounded-[24px] md:rounded-[40px]"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      </motion.div>
    );
  }

  // Trial just expired, account has not yet transitioned to 'grace' status
  // (daysRemaining clamps to 0 once trial_ends_at has passed; hasWriteAccess
  // is already false at this point, so this prevents a silent lockout while
  // the hourly cron job has not yet flipped subscription_status to 'grace').
  if (isInTrial && daysRemaining === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-6"
      >
        <Alert variant="destructive" className="bg-gradient-to-r from-red-50 to-rose-50 border-red-300">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-red-900 font-bold text-lg">
                ⏳ Your trial has ended
              </p>
              <p className="text-sm text-red-800 mt-1">
                Your account is transitioning to a <strong>read-only grace period</strong>.
                Upgrade now to keep full access to your estate management tools.
              </p>
              <p className="text-xs text-red-700 mt-2 font-medium">
                Your plan: {pricing.label} ({pricing.units})
              </p>
            </div>
            <Button
              onClick={onUpgrade}
              className="ml-4 bg-red-600 hover:bg-red-700 text-white shadow-lg"
              size="lg"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Upgrade to Continue
            </Button>
          </AlertDescription>
        </Alert>
      </motion.div>
    );
  }

  // Grace period: Urgent warning
  if (isInGrace) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-6"
      >
        <Alert variant="destructive" className="bg-gradient-to-r from-red-50 to-rose-50 border-red-300">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-red-900 font-bold text-lg">
                ⚠️ Grace period: {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left
              </p>
              <p className="text-sm text-red-800 mt-1">
                Your trial has ended. You're in <strong>read-only mode</strong>.
                Upgrade to resume managing issues and assignments.
              </p>
              <p className="text-xs text-red-700 mt-2 font-medium">
                Your plan: {pricing.label} ({pricing.units})
              </p>
            </div>
            <Button
              onClick={onUpgrade}
              className="ml-4 bg-red-600 hover:bg-red-700 text-white shadow-lg"
              size="lg"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Upgrade to Continue
            </Button>
          </AlertDescription>
        </Alert>
      </motion.div>
    );
  }

  // Expired: Full read-only message
  if (isExpired) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-6"
      >
        <Alert variant="destructive" className="bg-gradient-to-r from-gray-100 to-slate-100 border-gray-400">
          <AlertTriangle className="h-5 w-5 text-gray-700" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-gray-900 font-bold text-lg">
                Subscription Required
              </p>
              <p className="text-sm text-gray-700 mt-1">
                Your trial and grace period have ended. You can view all data but cannot make changes.
                Upgrade to resume full access.
              </p>
              <p className="text-xs text-gray-600 mt-2 font-medium">
                Your plan: {pricing.label} ({pricing.units})
              </p>
            </div>
            <Button
              onClick={onUpgrade}
              className="ml-4 bg-slate-900 hover:bg-slate-800 text-white shadow-lg"
              size="lg"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      </motion.div>
    );
  }

  return null;
}
